import React, {createContext, ReactNode, useContext, useState} from 'react';
import {Alert, AlertColor, Snackbar} from '@mui/material';

interface SnackbarContextType {
    showSnackbar: (message: string, severity?: AlertColor) => void;
    hideSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

interface SnackbarProviderProps {
    children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({children}) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<AlertColor>('info');

    const showSnackbar = (message: string, severity: AlertColor = 'info') => {
        if (!message) return;
        setMessage(message);
        setSeverity(severity);
        setOpen(true);
    };

    const hideSnackbar = () => {
        setOpen(false);
    };

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        hideSnackbar();
    };

    return (
        <SnackbarContext.Provider value={{showSnackbar, hideSnackbar}}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={3500}
                onClose={handleClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                sx={{mb: 2, mr: 2}}
            >
                <Alert
                    onClose={handleClose}
                    severity={severity}
                    sx={{
                        width: '100%',
                        boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.15)',
                        borderRadius: '8px',
                        '& .MuiAlert-icon': {
                            fontSize: '20px'
                        },
                        '& .MuiAlert-message': {
                            fontSize: '14px',
                            fontWeight: 500
                        }
                    }}
                >
                    {message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
};
